import { CreateAttachmentDependencies } from './createAttachment';
import handleCreateAttachmentNotification from './handleCreateAttachmentNotification';
import { onAttachmentCreationError } from './helpers/onAttachmentCreationHandlers';
import prepareNotificationAndExecuteSendRequest from './helpers/prepareNotificationAndExecuteSendRequest';
import createAttachmentFromAttachmentDataProviderService from '../services/createAttachmentFromAttachmentDataProvider';
import { DatapointStatus, PerformanceDatapoint } from 'owa-analytics';
import { CloudFile } from 'owa-attachment-file-types';
import { ClientAttachmentId } from 'owa-client-ids';
import { NotificationSubscription } from 'owa-notification';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import NotificationPayloadBase from 'owa-service/lib/contract/NotificationPayloadBase';
import { ClientItemId } from 'owa-client-ids';
import { action } from 'satcheljs/lib/legacy';
import {
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    getAttachCloudFileCompleteTimeInMS,
    setActionCompletePercent,
    startFakeProgressIndicator,
    stopFakeProgressIndicator,
} from 'owa-attachment-full-data';

export default action('createAttachmentFromAttachmentDataProvider')(
    async function createAttachmentFromAttachmentDataProvider(
        parentItemId: ClientItemId,
        attachmentId: ClientAttachmentId,
        attachment: AttachmentType,
        cancellationId: string,
        backingObject: CloudFile,
        attachmentState: AttachmentState,
        onAttachmentCreated: (attachment: AttachmentType) => void,
        onAttachmentCreationFailed: () => void,
        dependencies: CreateAttachmentDependencies
    ): Promise<void> {
        const perfDatapoint = new PerformanceDatapoint(
            'AttachmentCreationClassicFromAttachmentDataProvider'
        );
        perfDatapoint.addCustomData([backingObject.providerType]);

        const logDatapointAndHandleOnAttachmentCreated = (attachment: AttachmentType) => {
            onAttachmentCreated(attachment);
            perfDatapoint.end();
        };

        const logDatapointAndHandleOnAttachmentCreationFailed = (error: Error) => {
            onAttachmentCreationFailed();
            perfDatapoint.endWithError(DatapointStatus.ServerError, error);
        };

        const handleNotification = (notification: NotificationPayloadBase) => {
            handleCreateAttachmentNotification(
                notification,
                parentItemId,
                attachmentId,
                attachment,
                attachmentState,
                logDatapointAndHandleOnAttachmentCreated,
                logDatapointAndHandleOnAttachmentCreationFailed,
                dependencies
            );
        };

        const sendRequest = async (
            subscriptionId: string,
            subscription: NotificationSubscription,
            channelId: string,
            notificationHandler: (notification: NotificationPayloadBase) => void
        ) => {
            try {
                if (attachmentState.attachmentType === AttachmentStateType.Attachment) {
                    startFakeProgressIndicator(
                        getAttachCloudFileCompleteTimeInMS(backingObject.size),
                        0.05,
                        (completePercent: number) => {
                            setActionCompletePercent(
                                <AttachmentFullViewState>attachmentState,
                                completePercent
                            );
                        },
                        attachmentState.attachmentId
                    );
                }

                const guid: string = await createAttachmentFromAttachmentDataProviderService(
                    parentItemId,
                    cancellationId,
                    backingObject,
                    subscriptionId,
                    channelId,
                    false /* TODO 9990: support insert cloud files as inline */
                );

                if (!guid) {
                    throw new Error(
                        'Empty response for createAttachmentFromAttachmentDataProvider request'
                    );
                }
            } catch (error) {
                stopFakeProgressIndicator(attachmentState.attachmentId, false);
                onAttachmentCreationFailed();
                onAttachmentCreationError(
                    attachmentState,
                    dependencies.attachmentWellViewState,
                    subscription,
                    notificationHandler
                );
                return;
            }
        };

        prepareNotificationAndExecuteSendRequest(sendRequest, handleNotification);
    }
);
